import { setAllAnalyticsIdentifiersFromQuery } from '$lib/services/analytics/identifiers';
import { Analytics } from '$lib/services/analytics/client';
import { ApiService } from '$lib/services/api';
import { app } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import { EVENT_TYPES } from '$events';
import {
	getJSPath,
	hashElementPath,
	isCustomTrackedElement,
	trackedElems,
} from '$lib/utils/click-tracking';
import {
	COOKIES,
	COOKIES_CONSENT,
	EVENT_NAMES,
	LOCAL_STORAGE_KEYS,
	QUERY_PARAM_KEYS,
} from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { Sentry } from '$lib/services/sentry';
import Cookies from 'js-cookie';
import { get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { ProductExperienceService } from '../product-experience';

/** Max length of a `link_text` payload field before truncation. */
const LINK_TEXT_MAX_LEN = 200;

/** Scroll depth is reported at every multiple of this percent: 2, 4, 6 … 100. */
const PAGE_SCROLL_DEPTH_STEP_PERCENT = 2;

/**
 * Resolves page-relative click coordinates for `link_coordinates`.
 *
 * Prefers the event's `pageX`/`pageY` (document space, includes scroll).
 * Falls back to `clientX`/`clientY` plus `window.scrollX`/`scrollY` for
 * synthetic or partially-mocked events that only carry viewport coords.
 * @param {MouseEvent} [clickEvent] The originating mouse event.
 * @returns {{ x: number, y: number } | undefined}
 */
function resolvePageCoordinates(clickEvent) {
	if (!clickEvent) return undefined;

	/**
	 * @param {'scrollX' | 'scrollY'} axis The scroll axis to read.
	 * @returns {number} The document scroll offset, or `0` outside the browser.
	 */
	function readScrollOffset(axis) {
		if (typeof window === 'undefined') return 0;
		return window[axis] || 0;
	}

	/**
	 * @param {number | undefined} pageCoord Document-space coordinate.
	 * @param {number | undefined} clientCoord Viewport-space coordinate.
	 * @param {number} scrollOffset Scroll offset for the same axis.
	 * @returns {number | undefined} The document-space coordinate, when resolvable.
	 */
	function toPageCoordinate(pageCoord, clientCoord, scrollOffset) {
		if (typeof pageCoord === 'number') return pageCoord;
		if (typeof clientCoord !== 'number') return undefined;
		return clientCoord + scrollOffset;
	}

	const x = toPageCoordinate(clickEvent.pageX, clickEvent.clientX, readScrollOffset('scrollX'));
	const y = toPageCoordinate(clickEvent.pageY, clickEvent.clientY, readScrollOffset('scrollY'));

	if (x === undefined || y === undefined) return undefined;
	return { x, y };
}

/**
 * Current scroll depth snapped down to the nearest
 * {@link PAGE_SCROLL_DEPTH_STEP_PERCENT} mark.
 * @returns {number | undefined} The crossed mark (0–100), or `undefined` when
 * the document is not scrollable.
 */
function resolveScrollDepthMark() {
	const scrollableHeight =
		(document.documentElement?.scrollHeight || 0) - (window.innerHeight || 0);
	if (scrollableHeight <= 0) return undefined;

	const percentScrolled = ((window.scrollY || 0) / scrollableHeight) * 100;
	const boundedPercent = Math.min(100, Math.max(0, percentScrolled));
	return (
		Math.floor(boundedPercent / PAGE_SCROLL_DEPTH_STEP_PERCENT) * PAGE_SCROLL_DEPTH_STEP_PERCENT
	);
}

/**
 * PII-safe `link_text` extractor for `client.page_link_click`.
 *
 * Reads the visible label only — `innerText` first, then
 * `aria-label` as a fallback — collapsing whitespace and capping at
 * `LINK_TEXT_MAX_LEN` characters. The `value` attribute is
 * deliberately never read so the payload cannot leak typed input
 * (per the analytics-migration PII guardrail).
 * @param {HTMLElement} el The DOM element to extract link text from.
 * @returns {string | undefined}
 */
function extractLinkText(el) {
	if (!el) return undefined;
	const raw =
		(typeof el.innerText === 'string' && el.innerText) ||
		(typeof el.getAttribute === 'function' && el.getAttribute('aria-label')) ||
		'';
	const normalised = raw.replace(/\s+/g, ' ').trim();
	if (!normalised) return undefined;
	return normalised.length > LINK_TEXT_MAX_LEN
		? normalised.slice(0, LINK_TEXT_MAX_LEN)
		: normalised;
}

/**
 *
 */
class AnalyticsService {
	/**
	 * Module-level guard sourcing `is_initial_page_load` on the first
	 * `client.page_load` emission of this client load. Flips to `true`
	 * after the first `trackPageView` call; resets only on hard reload.
	 */
	static _pageLoadEmittedThisSession = false;

	/** Deepest `PAGE_SCROLL_DEPTH_STEP_PERCENT` mark already reported for this page. */
	static _pageScrollLastEmittedDepth = 0;

	/**
	 * Document-level click tracker. Walks up from `e.target` to the
	 * closest tracked element (anchor / button / custom `data-track-id`
	 * carrier), builds the schema-shaped `client.page_link_click` data
	 * slot, and dispatches it through `Analytics.log`.
	 * @param {MouseEvent} e - The click event.
	 * @returns {Promise<void>} Resolves once the event is dispatched.
	 */
	static async clickTrackingHandler(e) {
		e.stopPropagation();

		try {
			let targetElement = e.target;

			while (targetElement && targetElement !== document) {
				if (trackedElems.includes(targetElement.tagName) || isCustomTrackedElement(targetElement)) {
					break;
				}
				targetElement = targetElement.parentElement;
			}

			if (!targetElement || targetElement === document) return;

			const data = await AnalyticsService.buildPageLinkClickData(targetElement, e);
			await Analytics.log(EVENT_TYPES.CLIENT_PAGE_LINK_CLICK, data);
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Click Tracking Handler',
				},
			});
			console.error(err);
		}
	}

	/**
	 * Logs `client.page_scroll` once per {@link PAGE_SCROLL_DEPTH_STEP_PERCENT}
	 * depth mark the user crosses, carrying the mark that was reached. Marks
	 * already reported for the current page never repeat.
	 * @returns {void}
	 */
	static scrollTrackingHandler() {
		try {
			const depthMark = resolveScrollDepthMark();
			if (depthMark === undefined) return;
			if (depthMark <= AnalyticsService._pageScrollLastEmittedDepth) return;

			AnalyticsService._pageScrollLastEmittedDepth = depthMark;
			void Analytics.log(EVENT_TYPES.CLIENT_PAGE_SCROLL, {
				percentage_scrolled: depthMark,
			}).catch((err) => {
				Sentry.captureException(err, {
					extra: { action: 'Error: Scroll Tracking Handler' },
				});
			});
		} catch (err) {
			Sentry.captureException(err, {
				extra: { action: 'Error: Scroll Tracking Handler' },
			});
		}
	}

	/**
	 * Clears per-page scroll depth so SPA navigation does not reuse the
	 * previous page's depth. Call from `afterNavigate`, not `trackPageView`.
	 * @returns {void}
	 */
	static resetPageScrollTracking() {
		AnalyticsService._pageScrollLastEmittedDepth = 0;
	}

	/**
	 * Builds the `data` slot for `client.page_link_click` from the
	 * tracked DOM element + the originating click event. Used by both
	 * the global handler and the in-route `Analytics.log` callers
	 * (password-reset auto-redirect, cancel-subscription feedback,
	 * home-video volumechange).
	 * @param {HTMLElement} targetElement The clicked DOM element.
	 * @param {MouseEvent} [clickEvent] The originating mouse event.
	 * @returns {Promise<import('./client').EventData<'client.page_link_click'>>}
	 */
	static async buildPageLinkClickData(targetElement, clickEvent) {
		const elementPath = getJSPath(targetElement);
		let elementId = isCustomTrackedElement(targetElement);
		if (!elementId) {
			const hashedPath = await hashElementPath(elementPath);
			elementId = hashedPath.slice(0, 8);
		}

		const hrefTarget =
			typeof targetElement.getAttribute === 'function'
				? targetElement.getAttribute('href') || undefined
				: undefined;
		const linkType =
			typeof targetElement.tagName === 'string' ? targetElement.tagName.toLowerCase() : undefined;

		/** @type {import('./client').EventData<'client.page_link_click'>} */
		const data = {
			element_id: elementId,
			element_path: elementPath,
		};
		if (hrefTarget) data.href_target = hrefTarget;
		const linkText = extractLinkText(targetElement);
		if (linkText) data.link_text = linkText;
		if (linkType) data.link_type = linkType;
		const linkCoordinates = resolvePageCoordinates(clickEvent);
		if (linkCoordinates) data.link_coordinates = linkCoordinates;
		return data;
	}

	/**
	 * Logs an event to the server without relying on the store or the API.
	 * @param {object} payload - The payload to log.
	 * @param {string} deviceId - The device id to log.
	 * @param {boolean} preserveSessionDeviceId - Whether to preserve the session device id.
	 * @param {string | null} token - The token to use for the request headers.
	 * @returns {Promise<object>} - A promise that resolves when the event is logged.
	 */
	static async statelessEventLog(
		payload,
		deviceId = 'client_failure',
		preserveSessionDeviceId = true,
		token = ApiService.getToken()
	) {
		try {
			if (payload) {
				payload.app_version = __APP_VERSION__; //eslint-disable-line
			}

			return await ApiService.eventLog(
				{
					event: {
						event_type: EVENT_NAMES.DEBUG,
						event_payload: payload,
					},
				},
				window.fetch,
				(payload) =>
					ApiService.statelessPack(payload, { deviceId, preserveSessionDeviceId, aoflToken: token })
			);
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					action: 'Error: Fire Event Log (stateless)',
				},
			});
		}
	}

	/**
	 * @returns {string} The created page name.
	 */
	static createPageName() {
		if (location.pathname === '/') return 'home';
		return location.pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '-');
	}

	/**
	 * @param {string} userAgent - The user agent string to check.
	 * @returns {boolean|null} True if the user agent string includes 'isAutomatedTesting', false otherwise, null if the user agent is not a string.
	 */
	static isAutomatedTestSession(userAgent) {
		if (!userAgent || typeof userAgent !== 'string') return null;
		return userAgent.includes('isAutomatedTesting');
	}

	/**
	 * Sets up a click tracking listener on the document.
	 * @returns {void} A promise that resolves when the listener is set up.
	 */
	static setClickTrackingListener() {
		document.addEventListener('click', AnalyticsService.clickTrackingHandler);
	}

	/**
	 * Removes the click tracking listener.
	 */
	static removeClickTrackingListener() {
		document.removeEventListener('click', AnalyticsService.clickTrackingHandler);
	}

	/**
	 * Tracks a named debug metric via the legacy `marketing.client_debug` pipeline.
	 *
	 * MIGRATED: superseded by `Analytics.log(EVENT_TYPES.CLIENT_DATA_ANOMALY, …)`;
	 * kept for emergency rollback only and deleted by CLEANUP.md.
	 * @param {string} metricName - The name of the metric to track.
	 * @param {object} data - The data to track.
	 * @returns {Promise<object>}
	 */
	static async trackClientDebugMetric(metricName, data) {
		return await AnalyticsService.trackClientDebug({
			eventSubType: 'metric',
			metricName,
			data: data,
		});
	}

	/**
	 * Tracks a client debug event via the legacy `marketing.client_debug` pipeline.
	 *
	 * MIGRATED: superseded by `Analytics.log(EVENT_TYPES.CLIENT_DATA_ANOMALY, …)`;
	 * kept for emergency rollback only and deleted by CLEANUP.md.
	 * @param {object} [options] - Debug event options.
	 * @returns {Promise<object>}
	 */
	static async trackClientDebug(options = {}) {
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.DEBUG,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				data: {
					debug_info: { ...options },
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: 'regpath',
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					page_url: location.origin + location.pathname,
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks a client validation event.
	 *
	 * MIGRATED: superseded by `Analytics.log(EVENT_TYPES.FORM_SUBMISSION, …)`;
	 * kept for emergency rollback only and deleted by CLEANUP.md. Note that
	 * the legacy implementation accepts no arguments — every historical call
	 * site passed `{ form, component }` which this method silently dropped.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackClientValidation() {
		try {
			const appMeta = get(app)?.meta || {};
			const userToProductUuid = get(user)?.userToProductUuid || '';
			const event = {
				event_type: EVENT_NAMES.CLIENT_VALIDATION,
				device_id: getDeviceId(),
				event_payload: {
					aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
					user_to_product_uuid: userToProductUuid,
					data: {
						full_url: window.location.href,
						device_info: {
							...appMeta,
							user_agent: window.navigator?.userAgent,
							platform: window.navigator?.platform,
							vendor: window.navigator?.vendor,
							max_touch_points: window.navigator?.maxTouchPoints,
						},
						client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
						experiment_id: Cookies.get(COOKIES.EXPERIMENTS),
						track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
						site_section: 'regpath',
						is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
						page_url: location.origin + location.pathname,
					},
					url_variable_info: AnalyticsService.getUrlVariableInfo(),
					udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
					adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
				},
			};

			return await AnalyticsService.fireEvent({ event });
		} catch (error) {
			Sentry.captureException(error, {
				extra: { action: 'Error: Tracking Client Validation' },
			});
		}
	}

	/**
	 * @param {object} options - The options for tracking the third party conversion
	 * @param {?string} options.conversion_event_id - The conversion event id
	 * @param {?string} options.country_code - The country code
	 * @param {?string} options.email - The email
	 * @param {?object} options.event_info - The event info
	 * @param {?Array<string>} options.event_source - The event source
	 * @param {string} options.event_type - The event type
	 * @param {?string} [options.user_to_product_uuid] - The user to product uuid
	 * @returns {Promise<void>} A promise that resolves when the conversion is tracked
	 */
	static async trackThirdPartyConversion({
		conversion_event_id,
		country_code,
		email,
		event_info,
		event_source,
		event_type,
		user_to_product_uuid,
	}) {
		if (get(app)?.meta?.inApp) return;

		try {
			const payload = {
				conversion_event_id: conversion_event_id || uuidv4(),
				country_code: country_code || 'US',
				event_info: event_info || {},
				event_source: event_source || [],
				event_source_url: 'https://www.abcmouse.com',
				event_type: event_type,
				hashed_email: email ? await AnalyticsService.hashEmailAddress(email) : '',
				user_agent: window.navigator?.userAgent,
				platform: window.navigator?.platform,
				vendor: window.navigator?.vendor,
				max_touch_points: window.navigator?.maxTouchPoints,
				user_to_product_uuid: user_to_product_uuid || '',
			};

			return await ApiService.logThirdPartyConversion(payload);
		} catch (err) {
			Sentry.captureException(err, {
				extra: { action: 'Error: Track Third Party Conversion' },
			});
		}
	}

	/**
	 * Tracks a page view event via the new `@mkt-client/analytics` client.
	 *
	 * Signature kept intact so every existing caller (the global
	 * `afterNavigate` plus ~21 in-route emitters and view-state
	 * transitions) routes through `Analytics.log(EVENT_TYPES.CLIENT_PAGE_LOAD, …)`
	 * unchanged. Duplicate-emitter removal is tracked separately —
	 * see `.cursor/plans/follow-ups/page-load-duplicate-removal.md`.
	 *
	 * MIGRATED: legacy fan-out via `fireEvent` is removed; signature is
	 * preserved for the call-site parity window and deleted by CLEANUP.md.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.customPageName] - An optional custom page name.
	 * @param {string} [options.componentName] - Resolved route-condition key. Stored as `page_version` for this route's page loads.
	 * @returns {Promise<void>} A promise that resolves once the event is dispatched.
	 */
	static async trackPageView(options = {}) {
		const { customPageName, componentName } = options;
		if (componentName) {
			Analytics.setPageVersion(componentName);
		}
		const pageName = customPageName ?? AnalyticsService.createPageName();
		const isInitialPageLoad = !AnalyticsService._pageLoadEmittedThisSession;
		AnalyticsService._pageLoadEmittedThisSession = true;
		await Analytics.log(EVENT_TYPES.CLIENT_PAGE_LOAD, {
			page_name: pageName,
			page_title: typeof document !== 'undefined' ? document.title || '' : '',
			referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
			is_initial_page_load: isInitialPageLoad,
		});
	}

	/**
	 * Tracks a link entry event.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.source] - The source.
	 * @param {string} [options.type] - The type.
	 * @returns {Promise<object>} A promise that resolves when tracking completes.
	 */
	static async trackLinkEntry(options = {}) {
		const { source, type } = options;
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.LINK_ENTRY_VISITED,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				link_info: {
					source: source,
					type: type,
				},
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 *
	 * Tracks an email click event if email click query param is present.
	 * @returns {Promise<void>} A promise that resolves when the tracking is complete.
	 */
	static async trackEmailClick() {
		const urlSearchParam = new URLSearchParams(location.search);
		const emailClickParam = urlSearchParam.get(QUERY_PARAM_KEYS.EMAIL_CLICK);

		if (!emailClickParam || typeof emailClickParam !== 'string') return;

		const payload = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			request_url: location.href,
		};

		try {
			await ApiService.receipientLogEmailClick(payload);
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Analytics Service: Track email click',
				},
			});
		}
	}

	/**
	 *
	 * Fires internal pixels if email click query param is present.
	 * @param {string} event - The event to fire.
	 * @param {object} replacements - The replacements for the event.
	 * @returns {Promise<void>} A promise that resolves when the tracking is complete.
	 */
	static async fireInternalPixels(event, replacements = {}) {
		const { pixelsInternal } = await import('./pixels');

		if (pixelsInternal[event]) {
			const internalPixels = pixelsInternal[event];

			for (let i = 0; i < internalPixels.length; i++) {
				for (const key in replacements) {
					if (replacements.hasOwnProperty(key)) {
						const regex = new RegExp('{{' + key + '}}', 'gi');
						internalPixels[i] = internalPixels[i].replace(regex, replacements[key]);
					}
				}

				try {
					await fetch(internalPixels[i], {
						method: 'GET',
						headers: {
							'Content-type': 'application/json;charset=UTF-8',
							'Access-Control-Allow-Origin': '*',
						},
						mode: 'no-cors',
					});
				} catch (err) {
					Sentry.captureException(err, {
						extra: {
							action: 'Error: Analytics Service: Fire Internal Pixels',
						},
					});
				}
			}
		}
	}

	/**
	 * @returns {void}
	 */
	static thirdPartyPageLoad() {
		const page = AnalyticsService.createPageName();
		try {
			// Google Analytics
			if (window.gtag) {
				window.gtag('set', 'page', page);
				window.gtag('send', 'pageview');
			}

			// Bing UET
			window.uetq = window.uetq || [];
			window.uetq.push('event', 'page_view', { page_path: page });
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Third Party Page Load',
				},
			});
		}
	}

	/**
	 * @returns {object} The URL variable info
	 */
	static getUrlVariableInfo() {
		return Analytics.getUrlVariableInfo();
	}

	/**
	 * Get the hashed email address depending on the source tag and recipient domain
	 * Google requires specific email normalization for their conversion tracking
	 * @param {string} email - the email address to hash
	 * @returns {Promise<string>} A promise that resolves to the hashed email address.
	 */
	static async hashEmailAddress(email) {
		const sourceTag = AnalyticsService.getSourceTag();
		const normalizedEmail = sourceTag?.includes('goog')
			? AnalyticsService.normalizeGoogleEmail(email)
			: AnalyticsService.normalizeEmail(email);
		return await AnalyticsService.hashValue(normalizedEmail);
	}

	/**
	 * Normalizes an email address by trimming whitespace and converting to lowercase.
	 * @param {string} email - The email address to normalize.
	 * @returns {string} The normalized email address.
	 */
	static normalizeEmail(email) {
		return email.trim().toLowerCase();
	}

	/**
	 * Normalizes a Google email address by removing dots from the local part for gmail/googlemail domains.
	 * @param {string} email - The email address to normalize.
	 * @returns {string} The normalized email address.
	 */
	static normalizeGoogleEmail(email) {
		const normalized = AnalyticsService.normalizeEmail(email);
		const [localPart, domain] = normalized.split('@');
		const isGoogleDomain = domain && /^(gmail|googlemail)\.com\s*$/.test(domain);
		const cleanLocalPart = isGoogleDomain ? localPart.replace(/\./g, '') : localPart;
		return `${cleanLocalPart}@${domain}`;
	}

	/**
	 * @param {string} string string to hash
	 * @returns {Promise<string>} A promise that resolves to the hashed string
	 */
	static async hashValue(string) {
		const utf8 = new TextEncoder().encode(string);
		const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
		return hashHex;
	}

	/**
	 * Parses a full name into firstname and lastname components.
	 * @param {string} fullName - The full name to parse (e.g., "John Doe" or "Mary Jane Smith")
	 * @returns {{ firstname: string, lastname: string }} - Object containing firstname and lastname
	 */
	static parseFullName(fullName) {
		if (!fullName || typeof fullName !== 'string') {
			return { firstname: '', lastname: '' };
		}

		const trimmedName = fullName.trim();
		if (!trimmedName) {
			return { firstname: '', lastname: '' };
		}

		const nameParts = trimmedName.split(/\s+/);

		if (nameParts.length === 1) {
			// Single name provided
			return { firstname: nameParts[0], lastname: '' };
		}

		const firstname = nameParts[0];
		const lastname = nameParts.slice(1).join(' ');

		return { firstname, lastname };
	}

	/**
	 * Strips phone number formatting to return only digits.
	 * Rokt expects phone numbers in format: 1234567890 or +11234567890
	 * @param {string} phone - The phone number (e.g., "(123) 456-7890")
	 * @returns {string} - Phone with only digits
	 */
	static formatPhoneForGTM(phone) {
		if (!phone || typeof phone !== 'string') {
			return '';
		}

		return phone.replace(/\D/g, '');
	}

	/**
	 * Pushes data to the dataLayer for analytics tracking.
	 * @param {object} data - The data to be pushed to the dataLayer.
	 * @param {string} [data.currency] - The currency tied to the event(optional).
	 * @param {string} [data.email] - The hashed email of the user (optional).
	 * @param {string} [data.espRecipientID] - The ID of the email service provider recipient (optional).
	 * @param {string} data.eventName - The name of the event.
	 * @param {string} [data.event_id] - The ID of the event.
	 * @param {string} [data.fbc] - The facebook click ID (optional).
	 * @param {string} [data.fbp] - The facebook pixel ID (optional).
	 * @param {string} [data.firstname] - The user's first name (optional).
	 * @param {string} [data.language] - The language code (e.g., 'en', 'es') (optional).
	 * @param {string} [data.lastname] - The user's last name (optional).
	 * @param {string} [data.ltv] - The predicted lifetime value of the event initiator (optional).
	 * @param {string} [data.mobile] - The user's mobile phone number (optional).
	 * @param {string} [data.product] - The hash of the subscription plan associated with the event (optional).
	 * @param {string} [data.state] - The state of the user (optional).
	 * @param {string} [data.user_to_product_uuid] - The user to product uuid of the event initiator (optional).
	 * @param {string} [data.value] - The actual conversion amount paid at transaction (optional).
	 * @param {string} [data.zip] - The user's zip code from billing address (optional, CC only).
	 */
	static async dataLayerPush(data) {
		if (get(app)?.meta?.inApp) return;

		let experimentInfo = {};
		try {
			window.dataLayer = window.dataLayer || [];
			const appMeta = get(app)?.meta;
			const appGlobal = get(app)?.global || {};

			let deviceType = 'Desktop';
			if (appMeta.isIosApp || appMeta.isIosViewer) {
				deviceType = 'iOS';
			} else if (appMeta.isAndroidApp || appMeta.isAndroidViewer) {
				deviceType = 'Android';
			}

			try {
				const experimentsJson = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
				experimentInfo = JSON.parse(experimentsJson) || {};
			} catch (err) {
				Sentry.captureException(err, {
					extra: {
						action: 'Error: Decoding Experiment Info',
					},
				});
			}
			window.dataLayer.push({
				id: data?.event_id || uuidv4(),
				event: data?.eventName,
				eventData: {
					analyticsCookiesEnabled:
						appGlobal.analyticsCookiesEnabled || Cookies.get(COOKIES_CONSENT.ANALYTICS) === 'true',
					cjeCookie: Cookies.get(COOKIES.CJ_COOKIE) ?? '',
					clientVersion: __APP_VERSION__, //eslint-disable-line
					currency: data?.currency || 'USD',
					deviceType,
					espRecipientID: data?.espRecipientID ?? '',
					experimentInfo,
					externalID: data?.user_to_product_uuid
						? await AnalyticsService.hashValue(data?.user_to_product_uuid)
						: '',
					fbc: data?.fbc,
					fbp: data?.fbp,
					firstname: data?.firstname ?? '',
					geo: appMeta.viewerCountry,
					hashedEMail: data?.email ? await AnalyticsService.hashEmailAddress(data?.email) : '',
					inApp: appMeta.inApp,
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					language: data?.language ?? '',
					lastname: data?.lastname ?? '',
					ltv: data?.ltv,
					ltvCurrency: 'USD', // LTV always in USD
					mobile: data?.mobile ?? '',
					pageUrl: location.href,
					product: data?.product,
					sourceTag: AnalyticsService.getSourceTag(),
					state: data?.state ?? '',
					targetingCookiesEnabled:
						appGlobal.targetingCookiesEnabled || Cookies.get(COOKIES_CONSENT.TARGETING) === 'true',
					timestamp: Date.now(),
					userAgent: navigator.userAgent,
					value: data?.value ?? '',
					zip: data?.zip ?? '',
				},
			});
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Data Layer Push',
				},
			});
		}
	}

	/**
	 * Retrieves the source tag from the URL search parameters or local storage.
	 * If a sourceTag exists, stores other query parameters in localStorage along with the tag itself.
	 * If legacySourceTag exists, checks the dynamic info cookie and stores in localStorage along with the tag itself.
	 * @returns {string|null} The source tag or null if not found.
	 */
	static getSourceTag() {
		const searchParams = new URLSearchParams(location.search);
		const sourceTag = searchParams.get(QUERY_PARAM_KEYS.SOURCE_TAG);

		const legacyCookiesNames = [
			COOKIES.LEGACY_CAMPAIGN_ID_THREE,
			COOKIES.LEGACY_CAMPAIGN_ID_TWO,
			COOKIES.LEGACY_CAMPAIGN_ID,
		];

		let legacySourceTag = null;
		for (const cookieName of legacyCookiesNames) {
			const cookieValue = Cookies.get(cookieName);
			if (cookieValue) {
				legacySourceTag = cookieValue;
				break;
			}
		}
		const cachedSourceTag = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);

		if (sourceTag) {
			const queryParams = {};
			for (const [key, value] of searchParams.entries()) {
				queryParams[key] = value;
			}

			delete queryParams.src_tag;
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.SOURCE_INFO,
				JSON.stringify({
					id: sourceTag,
					values: queryParams,
				})
			);
			return sourceTag;
		}

		if (legacySourceTag) {
			let sourceInfo = {};
			const dynamicInfoLegacy = Cookies.get(COOKIES.LEGACY_CAMPAIGN_VARS);

			if (dynamicInfoLegacy) {
				try {
					sourceInfo = JSON.parse(decodeURIComponent(dynamicInfoLegacy));
				} catch (err) {
					Sentry.captureException(err, {
						extra: {
							action: 'Error: Decoding Legacy Source Tag',
						},
					});
				}
			}
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.SOURCE_INFO,
				JSON.stringify({
					id: legacySourceTag,
					values: sourceInfo,
				})
			);
			return legacySourceTag;
		}

		try {
			const tag = JSON.parse(cachedSourceTag);
			return tag?.id || null;
		} catch (err) {
			return cachedSourceTag || null;
		}
	}

	/**
	 * Attaches a tracking pixel to the document by creating an iframe and writing the pixel content into it.
	 * @param {string} pixel - The HTML content of the tracking pixel to be attached.
	 * @returns {Promise<void>} A promise that resolves when the iframe has successfully loaded the pixel.
	 */
	static attachPixel(pixel) {
		return new Promise((resolve, _) => {
			const iFrame = document.createElement('iframe');
			iFrame.width = 1;
			iFrame.height = 1;
			iFrame.loading = 'lazy';
			iFrame.fetchpriority = 'low';
			iFrame.frameborder = 0;
			iFrame.style.display = 'none';

			document.body.appendChild(iFrame);

			iFrame.addEventListener('load', function () {
				resolve();
			});

			iFrame.contentWindow.document.open();
			iFrame.contentWindow.document.write(pixel);
			iFrame.contentWindow.document.close();
		});
	}

	/**
	 * Fires a campaign pixel if the global privacy control is not enabled.
	 * Retrieves campaign information from local storage and sends it to the API service.
	 * If a pixel is returned, it tracks and attaches the pixel.
	 * @param {string} event - The event name to be tracked.
	 * @returns {Promise<void>} - A promise that resolves when the pixel has been processed.
	 * @throws Will capture and log any exceptions using Sentry.
	 */
	static async fireCampaignPixel(event) {
		if (
			(typeof navigator !== 'undefined' && navigator.globalPrivacyControl) ||
			get(app)?.meta?.inApp
		)
			return;
		const campaignInfo = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);
		if (campaignInfo) {
			const payload = {
				event,
				campaignInfo: JSON.parse(campaignInfo),
			};

			try {
				const response = await ApiService.resolvePixelInfo(payload);
				if (response && response.pixel) {
					await AnalyticsService.trackPixel('before', event, 'campaign');
					await AnalyticsService.attachPixel(response.pixel);
					await AnalyticsService.trackPixel('after', event, 'campaign');
				}
			} catch (e) {
				Sentry.captureException(e, {
					extra: {
						action: 'Error: Fire Campaign Pixel',
					},
				});
			}
		}
	}

	/**
	 * Tracks a pixel event by sending a payload to the analytics service.
	 * @param {string} action - The action associated with the pixel event.
	 * @param {string} event - The specific event to be tracked.
	 * @param {string} type - The type of the event.
	 * @returns {Promise<object>} - A promise that resolves when the event is successfully fired.
	 */
	static async trackPixel(action, event, type) {
		const campaignId = AnalyticsService.getSourceTag();

		const payload = {
			event_type: `${EVENT_NAMES.PIXEL_FIRE}-${action}`,
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				event,
				type,
				campaignId,
			},
		};

		return await AnalyticsService.fireEvent({ event: payload });
	}

	/**
	 * Tracks an event using the analytics service.
	 * @param {object} payload - The payload to track.
	 * @returns {Promise<object>} A promise that resolves when the event is tracked.
	 */
	static async fireEvent(payload) {
		try {
			if (payload?.event?.event_payload) {
				payload.event.event_payload.app_version = __APP_VERSION__; //eslint-disable-line
			}

			return await ApiService.eventLog(payload);
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					action: 'Error: Fire Event Log',
				},
			});
		}
	}

	static setCJCookie() {
		const searchParams = new URLSearchParams(location.search);
		const cjEvent = searchParams.get(QUERY_PARAM_KEYS.CJ_EVENT);
		if (cjEvent) {
			Cookies.set(COOKIES.CJ_COOKIE, cjEvent, COOKIES.CJ_CONFIG);
		}
	}

	/**
	 * Handles actions to be performed after navigation.
	 * @param {object} options - The options for the analytics
	 * @param {string} options.componentName - The name of the component
	 */
	static async afterNavigate({ componentName }) {
		AnalyticsService.resetPageScrollTracking();
		ProductExperienceService.setProductExperienceFromQuery();
		AnalyticsService.thirdPartyPageLoad();
		await AnalyticsService.trackPageView({ componentName });
		await AnalyticsService.dataLayerPush({ eventName: 'global' });
	}

	/**
	 * Handles actions to be performed when the component is mounted.
	 */
	static async onMount() {
		AnalyticsService.setCJCookie();
		ProductExperienceService.setProductExperienceFromQuery();
		setAllAnalyticsIdentifiersFromQuery();
		await AnalyticsService.trackCampaignClickFromSourceTag();
		AnalyticsService.setClickTrackingListener();
		await AnalyticsService.trackEmailClick();
	}

	/**
	 * Emits `client.campaign_click` and tags Sentry with the active
	 * source tag. No-op when no source tag is present.
	 * @returns {Promise<void>}
	 */
	static async trackCampaignClickFromSourceTag() {
		const sourceTag = AnalyticsService.getSourceTag();
		if (!sourceTag) return;
		Sentry.setTag('source', sourceTag);
		await Analytics.log(EVENT_TYPES.CLIENT_CAMPAIGN_CLICK, {
			source_tag: sourceTag,
			dynamic_info: AnalyticsService.getCachedSourceInfoValues(),
		});
	}

	/**
	 * @returns {Record<string, unknown>}
	 */
	static getCachedSourceInfoValues() {
		try {
			const cached = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);
			return cached ? (JSON.parse(cached)?.values ?? {}) : {};
		} catch {
			return {};
		}
	}

	/**
	 * Handles actions to be performed when the component is destroyed.
	 */
	static async onDestroy() {
		AnalyticsService.removeClickTrackingListener();
	}
}

export { AnalyticsService };
